import { createContext, useContext, useRef, useState } from "react";
import { MessagesQueue } from "../components/messages-queue/MessagesQueue";
import { resolveError } from "../shared/errorMessages";
import type { RootContextValue } from "../types/Context";
import { logClientError } from "../helpers/clientLogger";

export const MESSAGE_TYPE = {
  ERROR: "ERROR",
  SUCCESS: "SUCCESS",
} as const;

const RootContext = createContext<RootContextValue | null>(null);

const RootProvider = ({ children }: { children: React.ReactNode }) => {
  const [messages, setMessages] = useState<any[]>([]);
  const messageIdRef = useRef(0);

  function showError(error: unknown, options?: { autoDismiss?: boolean }) {
    logClientError(error, { surface: "toast" });
    const { message, known } = resolveError(error);
    const autoDismiss = options?.autoDismiss ?? known;
    setMessages((prev) => {
      const id = String(++messageIdRef.current);
      return [
        ...prev.slice(-4),
        { id, type: MESSAGE_TYPE.ERROR, message, autoDismiss },
      ];
    });
  }

  function showSuccess(message: string) {
    setMessages((prev) => {
      const id = String(++messageIdRef.current);
      return [...prev.slice(-10), { id, type: MESSAGE_TYPE.SUCCESS, message }];
    });
  }

  function removeMessage(id: string) {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }

  return (
    <RootContext.Provider value={{ showError, showSuccess }}>
      {children}
      <MessagesQueue messages={messages} removeMessage={removeMessage} />
    </RootContext.Provider>
  );
};

export { RootContext, RootProvider };

export const useRootContext = (): RootContextValue => {
  const ctx = useContext(RootContext);
  if (!ctx) throw new Error("useRootContext must be used inside RootProvider");
  return ctx;
};
